import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import debounce from "lodash/debounce";
import { useState } from "react";
import { RoleName, UserExtendedAttrs } from "../../interfaces/rbac";
import { getInitials } from "../Account/utils";
import { useSearchUsers, useUpdateWorkspaceUser } from "../Rbac/RbacQueries";
import { DialogProps } from "./useDialogs";

function WorkspaceRoleDialog({
  payload,
  open,
  onClose,
}: DialogProps<{
  workspaceId: string;
  workspaceName: string;
  workspaceUsers: string[];
}>) {
  const { mutateAsync: updateWorkspaceUser } = useUpdateWorkspaceUser();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    data: searchResults,
    isLoading,
    isError,
  } = useSearchUsers(searchQuery);
  const [selectedUsers, setSelectedUsers] = useState<UserExtendedAttrs[]>([]);
  const [role, setRole] = useState<RoleName>(RoleName.USER);

  const handleSearchChange = debounce((value: string) => {
    setSearchQuery(value);
  }, 300);

  const filteredUsers =
    searchResults?.filter(
      (user) => !payload.workspaceUsers.includes(user._id)
    ) ?? [];

  function getContent() {
    if (isError) {
      return (
        <Alert variant="outlined" severity="error">
          Une erreur est survenue
        </Alert>
      );
    } else {
      return (
        <Stack spacing={2}>
          <Typography fontSize="0.8rem">
            {"Vous invitez des utilisateurs dans l'espace de travail: "}
            <Typography fontSize="0.8rem" fontWeight="bold">
              {payload.workspaceName}
            </Typography>
          </Typography>
          <Typography fontWeight="bold">Nom, prénom ou email</Typography>
          <Autocomplete
            disablePortal
            multiple
            options={filteredUsers}
            value={selectedUsers}
            onChange={(event, value) => setSelectedUsers(value)}
            onInputChange={(event, value) => handleSearchChange(value)}
            getOptionLabel={(option) =>
              option.first_name + " " + option.last_name
            }
            renderOption={(props, option) => (
              <ListItem {...props}>
                <Stack direction="row" spacing={2}>
                  <Avatar>
                    {getInitials({
                      first_name: option.first_name,
                      last_name: option.last_name,
                    })}
                  </Avatar>
                  <Stack>
                    <Typography fontWeight="bold">
                      {option.first_name} {option.last_name}
                    </Typography>
                    <Typography fontSize="0.8rem" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Stack>
                </Stack>
              </ListItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Rechercher par nom, prénom ou email"
                multiline
                className="w-[200px]"
              />
            )}
            loading={isLoading}
            loadingText="Recherche en cours..."
            noOptionsText="Aucun utilisateur trouvé"
            filterOptions={(x) => x}
          />
          <Typography fontWeight="bold">Rôle</Typography>
          <Autocomplete
            disablePortal
            value={role}
            onChange={(event, value) => setRole(value as RoleName)}
            options={Object.values(RoleName)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Rôle" />
            )}
          />
        </Stack>
      );
    }
  }

  return (
    <Dialog onClose={() => onClose()} open={open}>
      <DialogTitle>Inviter un utilisateur dans l'espace de travail</DialogTitle>
      <DialogContent>{getContent()}</DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            selectedUsers.forEach((user) => {
              updateWorkspaceUser({
                userId: user._id,
                workspaceId: payload.workspaceId,
                role: role,
              });
            });
            onClose();
          }}
        >
          Ajouter
        </Button>
        <Button onClick={() => onClose()}>Annuler</Button>
      </DialogActions>
    </Dialog>
  );
}

export default WorkspaceRoleDialog;
